import { useCallback, useMemo } from 'react';

import { MarginInstrument } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';

import { DateTime } from 'luxon';

import { useMarginPricesStore } from './useMarginPricesStore';
import { STALE_PRICE_TIME } from '../../utils';
import { useMarginAccount } from '../account';
import { useMarginInstruments } from '../instruments';

const useMarginPrices = () => {
  const { prices: data } = useMarginPricesStore();
  const { currency } = useMarginAccount();
  const { selectedMarket, selectedMarketPrimary } = useMarginInstruments();

  const getMarketPrice = useCallback(
    (marketId: string) => {
      if (!data.length) return undefined;

      if (marketId) return data.find((d) => d.symbol === marketId);
    },
    [data],
  );

  const getMarketPriceByInstrument = useCallback(
    (instrument: MarginInstrument) => {
      if (!data.length) return undefined;

      return data.find((d) => d.marketUuid === instrument.uuid);
    },
    [data],
  );

  const selectedMarketPrice = useMemo(() => {
    if (!data.length || !selectedMarket) return undefined;
    return getMarketPriceByInstrument(selectedMarket);
  }, [data, getMarketPriceByInstrument, selectedMarket]);

  const getCurrencyRate = useCallback(
    (side: 'bid' | 'ask' = 'bid') => {
      if (!selectedMarketPrimary || !currency) return Big(0);
      const market = getMarketPrice(`${currency.code}${selectedMarketPrimary.code}`);

      return Big(market?.[side]);
    },
    [currency, getMarketPrice, selectedMarketPrimary],
  );

  const getPrice = useCallback(
    (fromSymbol: string, toSymbol: string, side: 'bid' | 'ask') => {
      const fromMarket = getMarketPrice(fromSymbol);
      const toMarket = getMarketPrice(toSymbol);

      if (!fromMarket || !toMarket) return Big(0);

      return Big(fromMarket[side]).div(toMarket[side]);
    },
    [getMarketPrice],
  );

  const isStalePriceData = useCallback(() => {
    if (!data.length) return false;

    const btcPrice = getMarketPrice('BTCUSD');

    if (!btcPrice) return true;

    return Big(DateTime.now().toMillis()).minus(btcPrice.timestamp).gte(STALE_PRICE_TIME);
  }, [data, getMarketPrice]);

  return {
    data,
    getCurrencyRate,
    getMarketPrice,
    getMarketPriceByInstrument,
    getPrice,
    isStalePriceData,
    selectedMarketPrice,
  };
};

export { useMarginPrices };
